import { useMemo } from "react";
import {
  ChemicalElementSymbol,
  NonElementQuantity,
  StrategyDashboardHeatData,
  useGetMaterialMetadataSetQuery,
} from "src/store/api/generatedApi";
import { useTenant } from "hooks/settings";
import { sorted } from "helpers";
import { useIsBehindFeatureFlag } from "hooks/useFeatureFlag";
import { useTenantData } from "contexts/tenant";
import { HeatsDataContextType } from "./HeatsDataContext";

const getMaterialNamesWithNonzeroMass = (
  data: StrategyDashboardHeatData
): Set<string> => {
  const names = new Set<string>();
  for (const heat of data.series) {
    for (const mix of heat.mixes) {
      for (const total of mix.mix.totals) {
        if (total.material_name && total.mass > 0) {
          names.add(total.material_name);
        }
      }
    }
  }
  return names;
};

export interface HeatsDataSourceState {
  data: StrategyDashboardHeatData | undefined;
  isFetching: boolean;
  isLoading: boolean;
  isError: boolean;
}

export const useHeatsDataContextValue = ({
  data,
  isFetching,
  isLoading,
  isError,
}: HeatsDataSourceState): HeatsDataContextType => {
  const tenantName = useTenant();

  const {
    data: materialsData,
    isError: isMaterialsError,
    isLoading: isMaterialsLoading,
  } = useGetMaterialMetadataSetQuery({
    tenantName,
    materialMetadataSetId: "default",
  });

  const sortedMaterials = useMemo(() => {
    if (!data || !materialsData) {
      return undefined;
    }
    const nonzeroMass = getMaterialNamesWithNonzeroMass(data);
    return sorted(
      materialsData.materials.filter(
        (m) => m.visibility === "visible" || nonzeroMass.has(m.name)
      ),
      (a) => a.ordering
    );
  }, [data, materialsData]);

  const hasLifts = useIsBehindFeatureFlag("scrap_eyes");

  const { recipe_type } = useTenantData();
  const hasBaskets = recipe_type == "bucket";

  const availableElements: (ChemicalElementSymbol | NonElementQuantity)[] =
    useMemo(() => data?.available_sample_constraints ?? [], [data]);

  const scrapEyeOnly = Object.keys(
    data?.available_mix_summaries ?? {}
  ).includes("liftStrikes");

  const hasPriceComparison = Object.keys(
    data?.available_summaries ?? {}
  ).includes("scrapEyePriceDiff");

  const combinedIsLoading = isLoading || isMaterialsLoading;
  const combinedIsError = isError || isMaterialsError;

  return useMemo(
    () => ({
      data,
      materials: sortedMaterials,
      isFetching,
      isLoading: combinedIsLoading,
      isError: combinedIsError,
      hasLifts,
      hasBaskets,
      scrapEyeOnly,
      hasPriceComparison,
      availableElements,
    }),
    [
      data,
      sortedMaterials,
      isFetching,
      combinedIsLoading,
      combinedIsError,
      hasLifts,
      hasBaskets,
      scrapEyeOnly,
      hasPriceComparison,
      availableElements,
    ]
  );
};
